import type { Availability, Match } from '@/types/tournament.model';
import { intersectAvailability } from '@/utils/availability-fit';

/**
 * Marker the backend writes into `match.description` while a dependent match is still waiting for
 * its source results: `DEPENDENT_MATCH:<matchAId>:<matchBId>[:<roleA>:<roleB>]`.
 *
 * It shows up in every group of 4. With the 2+2 format (`americanGroupFormat = WINNER_LOSER`, or
 * an extra classifier) match 3 is winner(m1) vs winner(m2) and match 4 is loser(m1) vs loser(m2),
 * and the roles are spelled out. Otherwise the pairing is crossed — winner of one vs loser of the
 * other — and the backend writes the legacy form without roles, which means WINNER/LOSER (see
 * `DependentMatchRef.parse` there).
 * Those two matches are created upfront with placeholder teams that carry **no players**, so the
 * card would otherwise render "Unknown team".
 *
 * Once the real teams are in, the backend rewrites the marker as `RESOLVED_DEPENDENT_MATCH:...`
 * (kept so it can re-resolve the match if an upstream result is edited). That prefix must NOT be
 * labelled: those matches already have real teams.
 */
const DEPENDENT_MATCH_PREFIX = 'DEPENDENT_MATCH';

export type DependentRole = 'WINNER' | 'LOSER';

export type DependentMatchRef = {
  /** Match whose result defines team1. */
  matchAId: string;
  /** Match whose result defines team2. */
  matchBId: string;
  roleA: DependentRole;
  roleB: DependentRole;
};

const isRole = (value: string): value is DependentRole =>
  value === 'WINNER' || value === 'LOSER';

/**
 * Parses the `DEPENDENT_MATCH` marker, roles or not. Returns null for anything else — no
 * description, a plain description, or the `RESOLVED_DEPENDENT_MATCH` variant.
 */
export function parseDependentMatch(
  description: string | undefined | null,
): DependentMatchRef | null {
  if (!description) return null;

  const parts = description.split(':');
  if (parts.length !== 3 && parts.length !== 5) return null;

  // The legacy form has no roles: it always means WINNER/LOSER.
  const [prefix, matchAId, matchBId, roleA = 'WINNER', roleB = 'LOSER'] = parts;
  if (prefix !== DEPENDENT_MATCH_PREFIX) return null;
  if (!matchAId || !matchBId) return null;
  if (!isRole(roleA) || !isRole(roleB)) return null;

  return { matchAId, matchBId, roleA, roleB };
}

const ROLE_LABEL: Record<DependentRole, string> = {
  WINNER: 'Ganador',
  LOSER: 'Perdedor',
};

/**
 * Labels for a dependent match's placeholder teams, e.g. `"Ganador Partido 1"`.
 *
 * The number is the source match's 1-based position in `groupMatchList` — the same numbering the
 * group cards show, so "Partido 1" points at a card the manager can actually find. When the source
 * match is not in the list (shouldn't happen, but the marker is just an id) the label degrades to
 * the bare role.
 *
 * Returns an empty object when the match is not a dependent one.
 */
export function getDependentTeamLabels(
  match: Pick<Match, 'description'>,
  groupMatchList: Pick<Match, 'id'>[],
): { team1?: string; team2?: string } {
  const ref = parseDependentMatch(match.description);
  if (!ref) return {};

  const labelFor = (matchId: string, role: DependentRole): string => {
    const index = groupMatchList.findIndex((m) => m.id === matchId);
    return index === -1
      ? ROLE_LABEL[role]
      : `${ROLE_LABEL[role]} Partido ${index + 1}`;
  };

  return {
    team1: labelFor(ref.matchAId, ref.roleA),
    team2: labelFor(ref.matchBId, ref.roleB),
  };
}

export type DependentSideAvailability = {
  /** Common window of the two teams that can reach this side (`undefined` = unknown). */
  availability: Availability[] | undefined;
  /** 1-based position of the source match in the group — the "Partido N" the cards show. */
  sourceMatchNumber: number;
};

/**
 * Availability for the sides of a dependent match that don't have their team yet.
 *
 * Until its source match is played, a side can be taken by either team of that match, so its
 * availability is the window both share (`intersectAvailability`). Once the source result is in,
 * the backend puts the real team there (it has players) and that side is left out: the caller
 * uses the team's own availability, as with any match.
 *
 * Returns an empty object for a non-dependent match, and leaves a side out when its source match
 * isn't in `groupMatchList` (the marker is just an id).
 */
export function getDependentSideAvailability(
  match: Pick<Match, 'description' | 'team1' | 'team2'>,
  groupMatchList: Pick<Match, 'id' | 'team1' | 'team2'>[],
  availabilityByTeamId: Map<string, Availability[]>,
): { team1?: DependentSideAvailability; team2?: DependentSideAvailability } {
  const ref = parseDependentMatch(match.description);
  if (!ref) return {};

  const availabilityOf = (team: Match['team1']) =>
    team ? availabilityByTeamId.get(team.id) : undefined;

  const sideFor = (
    team: Match['team1'],
    sourceMatchId: string,
  ): DependentSideAvailability | undefined => {
    if ((team?.players?.length ?? 0) > 0) return undefined;
    const index = groupMatchList.findIndex((m) => m.id === sourceMatchId);
    if (index === -1) return undefined;
    const source = groupMatchList[index];
    return {
      availability: intersectAvailability(availabilityOf(source.team1), availabilityOf(source.team2)),
      sourceMatchNumber: index + 1,
    };
  };

  return {
    team1: sideFor(match.team1, ref.matchAId),
    team2: sideFor(match.team2, ref.matchBId),
  };
}

/** Icon copy for a dependent side: there is no single team to name yet. */
export function dependentSideAvailabilityMessages(sourceMatchNumber: number): {
  ok: string;
  notOk: string;
} {
  return {
    ok: `Pueden las dos parejas del Partido ${sourceMatchNumber}.`,
    notOk: `Alguna pareja del Partido ${sourceMatchNumber} no declaró este horario.`,
  };
}
